import {useCallback, useMemo, useState} from 'react'
import {type Client} from '@atproto/lex'
import {type AtUriString} from '@atproto/syntax'
import {useQuery, useQueryClient} from '@tanstack/react-query'

import {fetchAccountPost} from '#/lib/api/account-posts'
import {
  PROVIDER_COMPOSITION_QUERY_META,
  type ProviderCompositionResult,
} from '#/lib/provider-composition'
import {useModerationOpts} from '#/state/preferences/moderation-opts'
import {useThreadPreferences} from '#/state/queries/preferences/useThreadPreferences'
import {
  composeAppViewProviderRead,
  requireComposedProviderValue,
} from '#/state/queries/provider-composition'
import {
  filterPublicPostsForViewer,
  hasDirectViewerBlock,
} from '#/state/queries/public-visibility'
import {
  enforceThreadViewerBlockBoundaries,
  filterThreadViewerBlockBoundaries,
  hydrateBlockedThreadItems,
} from '#/state/queries/usePostThread/blocked'
import {
  LINEAR_VIEW_BELOW,
  LINEAR_VIEW_BF,
  TREE_VIEW_BELOW,
  TREE_VIEW_BELOW_DESKTOP,
  TREE_VIEW_BF,
} from '#/state/queries/usePostThread/const'
import {type PostThreadContextType} from '#/state/queries/usePostThread/context'
import {
  createCacheMutator,
  getThreadPlaceholder,
} from '#/state/queries/usePostThread/queryCache'
import {
  buildThread,
  sortAndAnnotateThreadItems,
} from '#/state/queries/usePostThread/traversal'
import {
  createPostThreadOtherQueryKey,
  createPostThreadQueryKey,
  type ThreadItem,
  type UsePostThreadQueryResult,
} from '#/state/queries/usePostThread/types'
import {getThreadgateRecord} from '#/state/queries/usePostThread/utils'
import * as views from '#/state/queries/usePostThread/views'
import {
  useAppviewClient,
  useAppviewProviderClientFactory,
  useMaybePdsClient,
  usePublicAppviewClient,
  useSession,
} from '#/state/session'
import {getPublicAppviewClient} from '#/state/session/clients'
import {getAppViewProvidersForSurface} from '#/state/session/providers'
import {useMergeThreadgateHiddenReplies} from '#/state/threadgate-hidden-replies'
import {useBreakpoints} from '#/alf'
import {IS_WEB} from '#/env'
import {app} from '#/lexicons'
import * as bsky from '#/types/bsky'

const EMPTY_THREADGATE_HIDDEN_REPLIES = new Set<string>()

export * from '#/state/queries/usePostThread/context'
export {useUpdatePostThreadThreadgateQueryCache} from '#/state/queries/usePostThread/queryCache'
export * from '#/state/queries/usePostThread/types'

async function fetchThreadPostsWithPublicFallback(
  client: Client,
  publicClient: Client,
  uris: AtUriString[],
  signal?: AbortSignal,
): Promise<app.bsky.feed.defs.PostView[]> {
  let primary: app.bsky.feed.getPosts.$OutputBody
  try {
    primary = await client.call(app.bsky.feed.getPosts, {uris}, {signal})
  } catch (error) {
    try {
      const publicData = await publicClient.call(
        app.bsky.feed.getPosts,
        {uris},
        {signal},
      )
      return filterPublicPostsForViewer(client, publicData.posts)
    } catch {
      throw error
    }
  }

  const needsPublicRead =
    primary.posts.length < uris.length ||
    primary.posts.some(post => Boolean(post.author.viewer?.blockedBy))

  if (!needsPublicRead) return primary.posts

  try {
    const publicData = await publicClient.call(
      app.bsky.feed.getPosts,
      {uris},
      {signal},
    )
    const publicPosts = await filterPublicPostsForViewer(
      client,
      publicData.posts,
    )
    const primaryByUri = new Map(primary.posts.map(post => [post.uri, post]))
    const publicByUri = new Map(publicPosts.map(post => [post.uri, post]))

    return uris
      .map(uri => {
        const primaryPost = primaryByUri.get(uri)
        const publicPost = publicByUri.get(uri)
        if (!primaryPost) return publicPost
        // A local direct block remains authoritative. An incoming direct
        // relationship may be hydrated from the public read so it does not
        // become a universal public-read tombstone. Listblock state is
        // already inert and never reaches this branch as a block reason.
        if (hasDirectViewerBlock(primaryPost.author)) {
          return primaryPost
        }
        return publicPost ?? primaryPost
      })
      .filter((post): post is app.bsky.feed.defs.PostView => !!post)
  } catch {
    return primary.posts
  }
}

type PostThreadQueryParams = Partial<
  Omit<app.bsky.unspecced.getPostThreadV2.$Params, 'anchor'>
>

function fetchPostThread(
  client: Client,
  anchor: string,
  params: PostThreadQueryParams,
  signal?: AbortSignal,
): Promise<app.bsky.unspecced.getPostThreadV2.$OutputBody> {
  return client.call(
    app.bsky.unspecced.getPostThreadV2,
    {
      anchor: anchor as AtUriString,
      ...params,
    },
    {signal},
  )
}

function fetchPostThreadOther(
  client: Client,
  anchor: string,
  signal?: AbortSignal,
): Promise<app.bsky.unspecced.getPostThreadOtherV2.$OutputBody> {
  return client.call(
    app.bsky.unspecced.getPostThreadOtherV2,
    {anchor: anchor as AtUriString},
    {signal},
  )
}

export function usePostThread({anchor}: {anchor?: string}) {
  const qc = useQueryClient()
  const client = useAppviewClient()
  const publicClient = usePublicAppviewClient()
  const providerClientFactory = useAppviewProviderClientFactory()
  const pdsClient = useMaybePdsClient()
  const {currentAccount, hasSession} = useSession()
  const {gtPhone} = useBreakpoints()
  const moderationOpts = useModerationOpts()
  const mergeThreadgateHiddenReplies = useMergeThreadgateHiddenReplies()
  const {
    isLoaded: isThreadPreferencesLoaded,
    sort,
    setSort: baseSetSort,
    view,
    setView: baseSetView,
    curationView,
    setCurationView: baseSetCurationView,
  } = useThreadPreferences()
  const below = useMemo(() => {
    return view === 'linear'
      ? LINEAR_VIEW_BELOW
      : IS_WEB && gtPhone
        ? TREE_VIEW_BELOW_DESKTOP
        : TREE_VIEW_BELOW
  }, [view, gtPhone])

  const postThreadQueryKey = createPostThreadQueryKey({
    anchor,
    sort,
    view,
  })
  const postThreadOtherQueryKey = createPostThreadOtherQueryKey({
    anchor,
  })

  const query = useQuery<UsePostThreadQueryResult>({
    enabled: isThreadPreferencesLoaded && !!anchor && !!moderationOpts,
    meta: PROVIDER_COMPOSITION_QUERY_META,
    queryKey: postThreadQueryKey,
    async queryFn(ctx) {
      let data: app.bsky.unspecced.getPostThreadV2.$OutputBody
      let hasOtherReplies = false
      let providerComposition:
        | ProviderCompositionResult<app.bsky.unspecced.getPostThreadV2.$OutputBody>
        | undefined
      let threadClient = client
      let threadPublicClient = publicClient
      const threadParams = {
        branchingFactor: view === 'linear' ? LINEAR_VIEW_BF : TREE_VIEW_BF,
        below,
        sort,
      } satisfies PostThreadQueryParams
      try {
        const composed = await composeAppViewProviderRead(
          'threads',
          (providerClient, _provider, context) =>
            fetchPostThread(
              providerClient,
              anchor!,
              threadParams,
              context.signal,
            ),
          {
            access: 'account-scoped',
            clientForProvider: providerClientFactory,
            signal: ctx.signal,
          },
        )
        providerComposition = composed
        data = requireComposedProviderValue(composed)
        const selectedProviderId = composed.selectedProviderIds[0]
        const selectedProvider = getAppViewProvidersForSurface('threads').find(
          provider => provider.id === selectedProviderId,
        )
        threadClient = selectedProvider
          ? providerClientFactory(selectedProvider)
          : client
        threadPublicClient = selectedProvider
          ? getPublicAppviewClient(selectedProvider.endpoint)
          : publicClient
      } catch (error) {
        if (ctx.signal.aborted) throw error
        try {
          /*
           * Public thread data does not require the account's OAuth grant.
           * This matters for an otherwise valid session whose grant predates
           * an optional thread-read permission, and for links to an actor on
           * another PDS. Keep the public retry inside the same provider
           * composition boundary so provider choice and provenance remain
           * explicit instead of silently routing to a hard-coded endpoint.
           */
          const composed = await composeAppViewProviderRead(
            'threads',
            (providerClient, _provider, context) =>
              fetchPostThread(
                providerClient,
                anchor!,
                threadParams,
                context.signal,
              ),
            {access: 'public', signal: ctx.signal},
          )
          providerComposition = composed
          data = requireComposedProviderValue(composed)
          if (pdsClient && currentAccount?.did) {
            data = {
              ...data,
              thread: await enforceThreadViewerBlockBoundaries(
                client,
                data.thread,
              ),
            }
          }
          const selectedProvider = getAppViewProvidersForSurface(
            'threads',
          ).find(provider => provider.id === composed.selectedProviderIds[0])
          threadClient = selectedProvider
            ? getPublicAppviewClient(selectedProvider.endpoint)
            : publicClient
          threadPublicClient = threadClient
        } catch (publicError) {
          if (ctx.signal.aborted) throw publicError
          if (!pdsClient || !currentAccount?.did) throw error
          const directPost = await fetchAccountPost({
            pdsClient,
            appviewClient: client,
            actor: currentAccount.did,
            uri: anchor!,
          })
          if (!directPost) throw error
          data = {
            hasOtherReplies: false,
            thread: [views.postViewToThreadPlaceholder(directPost)],
          }
          providerComposition = undefined
        }
      }

      const missingAnchor = data.thread.some(
        item =>
          item.depth === 0 &&
          bsky.isType(app.bsky.unspecced.defs.threadItemNotFound, item.value),
      )
      if (missingAnchor && pdsClient && currentAccount?.did) {
        const directPost = await fetchAccountPost({
          pdsClient,
          appviewClient: client,
          actor: currentAccount.did,
          uri: anchor!,
        })
        if (directPost) {
          data = {
            hasOtherReplies: false,
            thread: [views.postViewToThreadPlaceholder(directPost)],
          }
          providerComposition = undefined
        }
      }

      const hydratedThread = await hydrateBlockedThreadItems(
        data.thread || [],
        uris =>
          fetchThreadPostsWithPublicFallback(
            threadClient,
            threadPublicClient,
            uris,
            ctx.signal,
          ),
      )

      /*
       * If we know we have additional replies, we'll set this to true.
       * `ctx.meta` is React Query option metadata and may be frozen by the
       * provider-composition cache marker. Keep pagination state local to this
       * query invocation instead of mutating that metadata object.
       */
      if (data.hasOtherReplies) {
        hasOtherReplies = true
      }

      /*
       * The generated views type `threadgate.record` as an opaque lex map and
       * brand at-uris, so the response is asserted to the exported result type
       * here; the record swap-in below and every downstream consumer of the
       * thread read that narrower contract.
       */
      const result = {
        thread: hydratedThread,
        threadgate: data.threadgate,
        hasOtherReplies,
        providerComposition,
      } as UsePostThreadQueryResult

      const record = getThreadgateRecord(result.threadgate)
      if (result.threadgate && record) {
        result.threadgate.record = record
      }

      return result
    },
    placeholderData() {
      if (!anchor) return
      const placeholder = getThreadPlaceholder(qc, anchor)
      /*
       * Always return something here, even empty data, so that
       * `isPlaceholderData` is always true, which we'll use to insert
       * skeletons.
       */
      const thread = placeholder ? [placeholder] : []
      return {thread, threadgate: undefined, hasOtherReplies: false}
    },
    select(data) {
      const record = getThreadgateRecord(data.threadgate)
      if (data.threadgate && record) {
        data.threadgate.record = record
      }
      return data
    },
  })

  const thread = useMemo(() => query.data?.thread || [], [query.data?.thread])
  const threadgate = useMemo(
    () => query.data?.threadgate,
    [query.data?.threadgate],
  )
  const hasOtherThreadItems = useMemo(
    () => !!query.data?.hasOtherReplies,
    [query.data?.hasOtherReplies],
  )
  const [otherItemsVisible, setOtherItemsVisible] = useState(false)

  /**
   * Creates a mutator for the post thread cache. This is used to insert
   * replies into the thread cache after posting.
   */
  const mutator = useMemo(
    () =>
      createCacheMutator({
        params: {view, below},
        postThreadQueryKey,
        postThreadOtherQueryKey,
        queryClient: qc,
      }),
    [qc, view, below, postThreadQueryKey, postThreadOtherQueryKey],
  )

  /**
   * If we have additional items available from the server and the user has
   * chosen to view them, start loading data
   */
  const additionalQueryEnabled = hasOtherThreadItems && otherItemsVisible
  const additionalItemsQuery = useQuery({
    enabled: additionalQueryEnabled,
    meta: PROVIDER_COMPOSITION_QUERY_META,
    queryKey: postThreadOtherQueryKey,
    async queryFn({signal}) {
      let data: app.bsky.unspecced.getPostThreadOtherV2.$OutputBody
      let threadClient = client
      let threadPublicClient = publicClient
      try {
        const providerClients = new Map<string, Client>()
        const composed = await composeAppViewProviderRead(
          'threads',
          async (providerClient, provider, context) => {
            providerClients.set(provider.id, providerClient)
            return fetchPostThreadOther(providerClient, anchor!, context.signal)
          },
          {
            access: 'account-scoped',
            clientForProvider: providerClientFactory,
            signal,
          },
        )
        data = requireComposedProviderValue(composed)
        const selectedProviderId = composed.selectedProviderIds[0]
        const selectedProvider = getAppViewProvidersForSurface('threads').find(
          provider => provider.id === selectedProviderId,
        )
        threadClient =
          (selectedProviderId && providerClients.get(selectedProviderId)) ||
          client
        threadPublicClient = selectedProvider
          ? getPublicAppviewClient(selectedProvider.endpoint)
          : publicClient
      } catch (error) {
        if (signal.aborted) throw error
        const composed = await composeAppViewProviderRead(
          'threads',
          (providerClient, _provider, context) =>
            fetchPostThreadOther(providerClient, anchor!, context.signal),
          {access: 'public', signal},
        )
        data = requireComposedProviderValue(composed)
        if (pdsClient && currentAccount?.did) {
          data = {
            ...data,
            thread: await filterThreadViewerBlockBoundaries(
              client,
              data.thread,
            ),
          }
        }
        const selectedProvider = getAppViewProvidersForSurface('threads').find(
          provider => provider.id === composed.selectedProviderIds[0],
        )
        threadClient = selectedProvider
          ? getPublicAppviewClient(selectedProvider.endpoint)
          : publicClient
        threadPublicClient = threadClient
      }
      return {
        ...data,
        thread: await hydrateBlockedThreadItems(data.thread || [], uris =>
          fetchThreadPostsWithPublicFallback(
            threadClient,
            threadPublicClient,
            uris,
            signal,
          ),
        ),
      }
    },
  })
  const serverOtherThreadItems: ThreadItem[] = useMemo(() => {
    if (!additionalQueryEnabled) return []
    if (additionalItemsQuery.isLoading) {
      return Array.from({length: 2}).map((_, i) =>
        views.skeleton({
          key: `other-reply-${i}`,
          item: 'reply',
        }),
      )
    } else if (additionalItemsQuery.isError) {
      /*
       * We could insert an special error component in here, but since these
       * are optional additional replies, it's not critical that they're shown
       * atm.
       */
      return []
    } else if (additionalItemsQuery.data?.thread) {
      const {threadItems} = sortAndAnnotateThreadItems(
        additionalItemsQuery.data.thread,
        {
          view,
          skipModerationHandling: true,
          threadgateHiddenReplies:
            curationView === 'author'
              ? mergeThreadgateHiddenReplies(threadgate?.record)
              : EMPTY_THREADGATE_HIDDEN_REPLIES,
          moderationOpts: moderationOpts!,
        },
      )
      return threadItems
    } else {
      return []
    }
  }, [
    view,
    additionalQueryEnabled,
    additionalItemsQuery,
    curationView,
    mergeThreadgateHiddenReplies,
    moderationOpts,
    threadgate?.record,
  ])

  /**
   * Sets the sort order for the thread and resets the additional thread items
   */
  const setSort: typeof baseSetSort = useCallback(
    nextSort => {
      setOtherItemsVisible(false)
      baseSetSort(nextSort)
    },
    [baseSetSort, setOtherItemsVisible],
  )

  /**
   * Sets the view variant for the thread and resets the additional thread items
   */
  const setView: typeof baseSetView = useCallback(
    nextView => {
      setOtherItemsVisible(false)
      baseSetView(nextView)
    },
    [baseSetView, setOtherItemsVisible],
  )
  const setCurationView: typeof baseSetCurationView = useCallback(
    nextView => {
      setOtherItemsVisible(false)
      baseSetCurationView(nextView)
    },
    [baseSetCurationView, setOtherItemsVisible],
  )

  /*
   * This is the main thread response, sorted into separate buckets based on
   * moderation, and annotated with all UI state needed for rendering.
   */
  const {threadItems, otherThreadItems} = useMemo(() => {
    return sortAndAnnotateThreadItems(thread, {
      view: view,
      threadgateHiddenReplies:
        curationView === 'author'
          ? mergeThreadgateHiddenReplies(threadgate?.record)
          : EMPTY_THREADGATE_HIDDEN_REPLIES,
      moderationOpts: moderationOpts!,
    })
  }, [
    thread,
    threadgate?.record,
    mergeThreadgateHiddenReplies,
    curationView,
    moderationOpts,
    view,
  ])

  /*
   * Take all three sets of thread items and combine them into a single thread,
   * along with any other thread items required for rendering e.g. "Show more
   * replies" or the reply composer.
   */
  const items = useMemo(() => {
    return buildThread({
      threadItems,
      otherThreadItems,
      serverOtherThreadItems,
      isLoading: query.isPlaceholderData,
      hasSession,
      hasOtherThreadItems,
      otherItemsVisible,
      showOtherItems: () => setOtherItemsVisible(true),
    })
  }, [
    threadItems,
    otherThreadItems,
    serverOtherThreadItems,
    query.isPlaceholderData,
    hasSession,
    hasOtherThreadItems,
    otherItemsVisible,
    setOtherItemsVisible,
  ])

  return useMemo(() => {
    const context: PostThreadContextType = {
      postThreadQueryKey,
      postThreadOtherQueryKey,
    }
    return {
      context,
      state: {
        /*
         * Copy in any query state that is useful
         */
        isFetching: query.isFetching,
        isPlaceholderData: query.isPlaceholderData,
        error: query.error,
        /*
         * Other state
         */
        sort,
        view,
        curationView,
        otherItemsVisible,
      },
      data: {
        items,
        threadgate,
        providerComposition: query.data?.providerComposition,
      },
      actions: {
        /*
         * Copy in any query actions that are useful
         */
        insertReplies: mutator.insertReplies,
        refetch: query.refetch,
        /*
         * Other actions
         */
        setSort,
        setView,
        setCurationView,
      },
    }
  }, [
    query,
    mutator.insertReplies,
    otherItemsVisible,
    sort,
    view,
    setSort,
    setView,
    setCurationView,
    curationView,
    threadgate,
    items,
    postThreadQueryKey,
    postThreadOtherQueryKey,
  ])
}
